// Core
import React, {
  ReactNode,
  useEffect,
  useState,
} from 'react';

// Libraries
import { useTranslation } from 'react-i18next';

// Hooks
import { useBreakpoint } from '../../../hooks/useBreakpoint';

// Components
import { Alert } from '../Alert';
import {
  Button,
  ButtonSize,
  ButtonType,
} from '../Button';
import { Col } from '../Col';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../Paragraph';
import { Row } from '../Row';
import { Close } from '../../icons';

// Component
import {
  ActionsPreviewContainer,
  Element,
  Footer,
  GradientEffect,
  Header,
  Overlay,
  PreviewContainer,
  PreviewImage,
  TitleContainer,
} from './Drawer.style';
import {
  DrawerPreviewProps,
  DrawerProps,
  DrawerSize,
  HeaderDrawerProps,
} from './Drawer.types';

function HeaderDrawer({
  alert = false,
  onClose,
  title,
}: HeaderDrawerProps): JSX.Element {
  // Dependencies
  const { isXs } = useBreakpoint();

  return (
    <Row>
      <Header>
        <TitleContainer>
          <Paragraph
            size={isXs ? ParagraphSize.lg : ParagraphSize.xl}
            weight={ParagraphWeight.bold}
          >
            {title}
          </Paragraph>
          <Button
            type={ButtonType.secondary}
            onClick={onClose}
            square
            size={ButtonSize.xs}
          >
            <Close />
          </Button>
        </TitleContainer>
        {alert && (
          <Row>
            <Col>
              <Alert
                icon={alert.icon}
                testId="drawer-header-alert"
                description={alert.description}
                closable={false}
                type={alert.type}
              />
            </Col>
          </Row>
        )}
      </Header>
    </Row>
  );
}

function Preview({
  previewImage,
  primaryPreviewAction,
  secondaryPreviewAction,
  title,
}: DrawerPreviewProps): JSX.Element {
  return (
    <PreviewContainer>
      <Col cols={12}>
        <PreviewImage src={previewImage} alt={title} />
      </Col>
      <ActionsPreviewContainer cols={12}>
        {secondaryPreviewAction && (
          <Button
            type={ButtonType.secondary}
            square
            onClick={secondaryPreviewAction.onClick}
          >
            {secondaryPreviewAction.children}
          </Button>
        )}
        {primaryPreviewAction && (
          <Button
            square
            onClick={primaryPreviewAction.onClick}
            type={ButtonType.primary}
          >
            {primaryPreviewAction.children}
          </Button>
        )}
      </ActionsPreviewContainer>
    </PreviewContainer>
  );
}

function Drawer({
  alert = false,
  children,
  className,
  footer = 'default',
  onClose,
  open = false,
  preview,
  primaryAction,
  secondaryAction,
  size = DrawerSize.md,
  testId,
  title,
}: DrawerProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const {
    isXs,
    isSm,
    isMd,
    isLg,
    isXl,
  } = useBreakpoint();

  /* ***********************************************************************************************
  ****************************************** LOCAL STATE *******************************************
  *********************************************************************************************** */

  interface State {
    hasScroll: boolean;
    showGradient: boolean;
  }

  const initialState: State = {
    hasScroll: false,
    showGradient: false,
  };

  const [state, setState] = useState<State>(initialState);

  /* ***********************************************************************************************
  ******************************************* METHODS **********************************************
  *********************************************************************************************** */

  let renderedFooter: ReactNode;

  if (footer === 'default') {
    renderedFooter = (
      <Footer className="drawer-footer">
        {secondaryAction && (
          <Button
            onClick={secondaryAction.onClick}
            block={false}
            disabled={secondaryAction.disabled}
            testId="drawer-secondary-action"
            type={ButtonType.link}
          >
            {secondaryAction?.children || t('layout.elements.drawer.defaultSecondaryButtonText')}
          </Button>
        )}
        {primaryAction && (
          <Button
            onClick={primaryAction.onClick}
            block
            disabled={primaryAction.disabled}
            testId="drawer-primary-action"
            type={ButtonType.primary}
            loading={primaryAction.loading}
            loadingText={primaryAction.loadingText}
          >
            {primaryAction?.children || t('layout.elements.drawer.defaultPrimaryButtonText')}
          </Button>
        )}
      </Footer>
    );
  } else if (footer === false) {
    renderedFooter = undefined;
  } else {
    renderedFooter = footer;
  }

  /* ***********************************************************************************************
  ************************************* COMPONENT HANDLING *****************************************
  *********************************************************************************************** */

  // Sets up a scroll listener on the element with the class '.ant-drawer-body'.
  useEffect(() => {
    const drawerElement: HTMLElement | null = document.querySelector('.ant-drawer-body');

    const checkScrollState = (): void => {
      if (drawerElement) {
        const hasScrollableContent: boolean = (
          drawerElement.scrollHeight > drawerElement.offsetHeight
        );

        if (hasScrollableContent) {
          const isAtEnd: boolean = (
            drawerElement.scrollTop + drawerElement.offsetHeight + 5
          ) >= drawerElement.scrollHeight;

          drawerElement.classList.add('has-scroll');
          setState((prevState: State) => ({
            ...prevState,
            hasScroll: true,
            showGradient: !isAtEnd,
          }));
        } else {
          drawerElement.classList.remove('has-scroll');
          setState((prevState: State) => ({
            ...prevState,
            hasScroll: false,
            showGradient: false,
          }));
        }
      }
    };

    const handleScroll = (): void => {
      checkScrollState();
    };

    let cleanup: (() => void) | undefined;
    if (drawerElement) {
      // Check initial state
      checkScrollState();

      drawerElement.addEventListener('scroll', handleScroll);
      cleanup = () => {
        drawerElement.removeEventListener('scroll', handleScroll);
        drawerElement.classList.remove('has-scroll');
      };
    }

    return cleanup;
  }, []);

  const getWidthBySize = (drawerSize: DrawerSize): string => {
    if (isXs) {
      // Mobile: 90-95% the width
      return drawerSize === DrawerSize.lg ? '95%' : '90%';
    }
    if (isSm || isMd) {
      // Tablet: 60-70% the width
      return drawerSize === DrawerSize.lg ? '70%' : '60%';
    }
    if (isLg || isXl) {
      // Tablet grande: larger sizes for better content display
      return drawerSize === DrawerSize.lg ? '55%' : '45%';
    }
    // Desktop XXL (1600px+): original sizes
    return drawerSize === DrawerSize.lg ? '39%' : '39%';
  };

  const mapWidthBySize: Record<DrawerSize, string> = {
    [DrawerSize.md]: getWidthBySize(DrawerSize.md),
    [DrawerSize.lg]: getWidthBySize(DrawerSize.lg),
  };

  return (
    <>
      <Overlay open={open} />
      <Element
        rootClassName={className}
        data-testid={testId}
        forceRender
        title={(
          <HeaderDrawer
            title={title}
            onClose={onClose}
            alert={alert}
          />
        )}
        placement="right"
        closeIcon={null}
        onClose={onClose}
        open={open}
        contentWrapperStyle={{
          top: '0.75em',
          right: '0.75em',
          bottom: '0.75em',
          borderRadius: '0.25em',
          overflow: 'hidden',
          width: mapWidthBySize[size],
        }}
        footer={renderedFooter}
      >
        {preview && (
          <Preview
            previewImage={preview.previewImage}
            primaryPreviewAction={preview.primaryPreviewAction}
            secondaryPreviewAction={preview.secondaryPreviewAction}
            title={preview.title}
          />
        )}
        <div>
          {children}
        </div>
        {state.showGradient && (
          <GradientEffect
            data-testid="gradient-effect-drawer"
            isHidden={!state.showGradient}
          />
        )}
      </Element>
    </>
  );
}

export { Drawer };
